import { useRouter } from 'next/router'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import styled from 'styled-components'
import { Brand, User } from 'types/graphql'
import { media } from 'utils/media'
import { Text } from 'lib/Text'
import { useCurrentUser } from 'modules/auth/useCurrentUser'
import { UserPreviewLink } from 'modules/community/UserPreviewLink'
import { StarSelector } from 'modules/reviews/form/components/StarSelector'

const Row = styled.article`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
  border: 1px solid var(--black-20);
  box-shadow: 6px 6px 0px #f7f7f7;
  margin-top: 32px;
  ${media.phoneDown} {
    padding: 12px 20px;
  }
`

const FormContainer = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 24px;
  margin-top: 0;
  ${media.phoneDown} {
    flex-grow: 1;
    gap: 8px 16px;
  }
`

const UserRowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--wide-grid-spacing);

  ${media.phoneDown} {
    display: none;
  }
`

const UserRow = ({ user }: { user: Pick<User, 'fullName' | 'username' | 'avatarFullPath' | 'reviewCount'> }) => (
  <UserRowContainer>
    <UserPreviewLink
      name={user.fullName}
      username={user.username}
      reviewCount={user.reviewCount}
      avatarFullPath={user.avatarFullPath}
      showPopoverOnHover={false}
    />
  </UserRowContainer>
)

export const InlineReviewForm = ({ brand }: { brand: Pick<Brand, 'id' | 'primaryImageFullPath' | 'slug' | 'title'> }) => {
  const { user } = useCurrentUser()
  const formMethods = useForm()
  const { push } = useRouter()
  const handleRatingChange = (rating: number, brandSlug: string) => {
    push(`/brands/${brandSlug}/review?stars=${rating}&from=no-reviews`)
    return
  }

  return (
    <Row key={brand.id}>
      {user && <UserRow user={user} />}
      <FormContainer>
        <FormProvider {...formMethods}>
          <Text weight="semi" size="ps">
            Start a review
          </Text>
          <StarSelector size="small" onRatingChange={(rating) => handleRatingChange(rating, brand.slug)} showHint={false} />
        </FormProvider>
      </FormContainer>
    </Row>
  )
}
